import { TextArea } from "@amzn/storm-ui";
import React from "react";
import { ErrorBoundary } from "react-error-boundary";
import styled from "styled-components";

import type {
    DimensionMetadata,
    DimensionTemplate,
    DimensionTemplates,
} from "../../types/dimensions";
import { DimensionTypes } from "../../types/dimensions";
import { ErrorPromptDetails } from "../../utils/app.utils";

import { DataDimension } from "./DataDimension";
import { DateDimension } from "./DateDimension";
import DropdownDimension from "./DropdownDimension";
import { InputDimension } from "./InputDimension";
import { ListBuilderDimension } from "./ListBuilderDimension";
import { MarkdownDimension } from "./MarkdownDimension";
import { MultiSelectDimension } from "./MultiSelectDimension";
import { MultiSelectStaticDimension } from "./MultiSelectStaticDimension";
import { OpportunityDropdownDimension } from "./OpportunityDropdownDimension";
import { StaticDropdownDimension } from "./StaticDropdownDimension";
import { TargetSegmentPickerDimension } from "./vitest/TargetSegmentPickerDimension";

const dimensionComponentTypeMapper: {
    [key in DimensionTypes]: React.FunctionComponent<any>;
} = {
    [DimensionTypes.INPUT]: InputDimension,
    [DimensionTypes.DROPDOWN]: DropdownDimension,
    [DimensionTypes.OPPORTUNITY_DROPDOWN]: OpportunityDropdownDimension,
    [DimensionTypes.STATIC_DROPDOWN]: StaticDropdownDimension,
    [DimensionTypes.DATE]: DateDimension,
    [DimensionTypes.MARKDOWN]: MarkdownDimension,
    [DimensionTypes.DATA]: DataDimension,
    [DimensionTypes.MULTISELECT]: MultiSelectDimension,
    [DimensionTypes.MULTI_SELECT_STATIC]: MultiSelectStaticDimension,
    [DimensionTypes.TARGET_SEGMENT_PICKER]: TargetSegmentPickerDimension,
    [DimensionTypes.LIST_BUILDER]: ListBuilderDimension,
};

const DimensionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    max-width: 1000px;
`;

export const DimensionGroup = ({
    templates,
    hasAdvertiser,
    enableDebugMode,
    dimensionMetadata,
}: {
    templates: DimensionTemplates[];
    hasAdvertiser: boolean;
    enableDebugMode?: boolean;
    dimensionMetadata?: DimensionMetadata;
}): JSX.Element => {
    return (
        <DimensionsContainer>
            {templates.map((template) => (
                <div key={template.id}>
                    {enableDebugMode && (
                        <TextArea
                            id={`dimensions-debug-${template.id}`}
                            label={`DIMENSION ID: ${template.id}`}
                            fullWidth
                            disabled
                            value={JSON.stringify(template, null, 4)}
                            style={{ height: 200 }}
                        />
                    )}
                    <ErrorBoundary
                        fallbackRender={({ error }) => (
                            <ErrorPromptDetails
                                error={error}
                                title={`Dimension ${template.id} Error`}
                            />
                        )}
                    >
                        <DimensionComponent
                            key={template.id}
                            {...template}
                            configuration={{
                                ...template.configuration,
                                isAdvertiserScoped:
                                    template.configuration.isAdvertiserScoped ??
                                    hasAdvertiser,
                            }}
                            dimensionMetadata={dimensionMetadata}
                            debug={enableDebugMode}
                        />
                    </ErrorBoundary>
                </div>
            ))}
        </DimensionsContainer>
    );
};

const DimensionComponent = (props: DimensionTemplate): JSX.Element | null => {
    const Component = dimensionComponentTypeMapper[props.type];
    return Component !== undefined ? <Component {...props} /> : null;
};
